import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static values = { followSystem: Boolean }

    toggle(event) {
        const current = document.documentElement.dataset.bsTheme
        const next = current === "dark" ? "light" : "dark"

        this.applyTheme(next, {
            animate: true,
            origin: this.transitionOrigin(event?.currentTarget)
        })
    }

    connect() {
        if (this.followSystemValue) {
            this.systemAppearance = window.matchMedia("(prefers-color-scheme: dark)")
            this.syncSystemAppearance = () => this.applyTheme(this.systemAppearance.matches ? "dark" : "light")
            this.systemAppearance.addEventListener("change", this.syncSystemAppearance)
            document.addEventListener("visibilitychange", this.syncSystemAppearance)
            window.addEventListener("pageshow", this.syncSystemAppearance)
            this.syncSystemAppearance()
            return
        }

        const saved = localStorage.getItem("theme")
        if (saved) {
            this.applyTheme(saved)
        } else {
            const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches
            this.applyTheme(prefersDark ? "dark" : "light")
        }
    }

    disconnect() {
        if (!this.systemAppearance) return

        this.systemAppearance.removeEventListener("change", this.syncSystemAppearance)
        document.removeEventListener("visibilitychange", this.syncSystemAppearance)
        window.removeEventListener("pageshow", this.syncSystemAppearance)
    }

    applyTheme(theme, { animate = false, origin = null } = {}) {
        const root = document.documentElement

        if (!animate || !this.canAnimateThemeChange()) {
            root.dataset.bsTheme = theme
            if (!this.followSystemValue) localStorage.setItem("theme", theme)
            return
        }

        const point = origin || this.defaultOrigin()
        this.prepareTransition(root, point, theme)

        const transition = document.startViewTransition(() => {
            root.dataset.bsTheme = theme
            if (!this.followSystemValue) localStorage.setItem("theme", theme)
        })

        transition.finished.finally(() => {
            root.classList.remove("theme-transitioning", "theme-transition-light", "theme-transition-dark")
        })
    }

    canAnimateThemeChange() {
        return typeof document.startViewTransition === "function" &&
            !window.matchMedia("(prefers-reduced-motion: reduce)").matches
    }

    prepareTransition(root, origin, theme) {
        root.style.setProperty("--theme-toggle-x", `${origin.x}px`)
        root.style.setProperty("--theme-toggle-y", `${origin.y}px`)
        root.style.setProperty("--theme-toggle-radius", `${this.transitionRadius(origin)}px`)
        root.classList.add("theme-transitioning", `theme-transition-${theme}`)
        root.classList.remove(theme === "dark" ? "theme-transition-light" : "theme-transition-dark")
    }

    transitionOrigin(element) {
        if (!element) return this.defaultOrigin()

        const rect = element.getBoundingClientRect()
        return {
            x: rect.left + (rect.width / 2),
            y: rect.top + (rect.height / 2)
        }
    }

    defaultOrigin() {
        return {
            x: window.innerWidth - 80,
            y: 32
        }
    }

    transitionRadius(origin) {
        const corners = [
            [0, 0],
            [window.innerWidth, 0],
            [0, window.innerHeight],
            [window.innerWidth, window.innerHeight]
        ]

        return Math.ceil(Math.max(...corners.map(([x, y]) => Math.hypot(x - origin.x, y - origin.y))))
    }
}
